import { useQuery } from '@tanstack/react-query';
import { ungzip } from 'pako';
import Papa from 'papaparse';
import { S3SignedUrlFetch } from '../../../utils/fetch-utils';

export interface DailyPlacementData {
  est_day: string;
  network: string;
  platform: string;
  isci: string;
  pixel_impression_count: number;
  app_reported_impression_count: number;
  percentage_difference: number | null;
}

interface ParsedPlacementData {
  est_day: string;
  network: string;
  platform: string;
  isci: string;
  pixel_impression_count: string;
  app_reported_impression_count: string;
  percentage_difference: string;
}

const VALIDATE_PLACEMENT_S3_URI = 'bpm-spectrum/validate_placements/000.csv.gz';

const parseCount = (strCount: string): number =>
  Number.isNaN(Number(strCount)) ? 0 : Number(strCount);

const parseDiff = (strDiff: string): number | null => {
  const n = Number(strDiff);
  return strDiff === '' || Number.isNaN(n) ? null : n;
};

const transformParsedRow = (row: ParsedPlacementData): DailyPlacementData => ({
  ...row,
  pixel_impression_count: parseCount(row.pixel_impression_count),
  app_reported_impression_count: parseCount(row.app_reported_impression_count),
  percentage_difference: parseDiff(row.percentage_difference)
});

export const useValidatePlacement = (): ReturnType<typeof useQuery<DailyPlacementData[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['validatePlacement'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<DailyPlacementData[]> => {
      const res = await S3SignedUrlFetch(VALIDATE_PLACEMENT_S3_URI);
      const arrayBuffer = await res.arrayBuffer();
      const compressedData = new Uint8Array(arrayBuffer);
      const uncompressedCsvString = ungzip(compressedData, { toText: true });
      const { data } = Papa.parse<ParsedPlacementData>(uncompressedCsvString, { header: true });
      return data.map(transformParsedRow);
    }
  });
